import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Box, Chip, TextField, Pagination, InputAdornment, Button, Tooltip } from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import { ILicenseSimpleProps, ILicenseUpdatePayload } from '../../../services/LicenseService/types';
import { useDebounce } from '../../../services/SchoolTransportation/hooks/useDebounce';
import LicenseCard from './LicenseCard';

interface LicenseListProps {
    licenses: ILicenseSimpleProps[];
    isSingleMode?: boolean;
    liveCounters?: {
        devicesAmount: number;
        studentsAmount: number;
        responsiblesAmount: number;
    } | null;
    canEditValues?: boolean;
    onUpdateLicenseValues?: (licenseId: string, payload: ILicenseUpdatePayload) => Promise<void>;
}

type EditableField = 'studentUnitValue' | 'totalValue';

interface DraftValues {
    studentUnitValue: number;
    totalValue: number;
    isDirty: boolean;
    isSaving: boolean;
    changedField?: EditableField;
    error?: string;
}

const ADMIN_CARD_MIN_WIDTH = 260;
const currencyFormatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
});

const excelDateFormatter = new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
});

const escapeExcelValue = (value: unknown) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const formatExcelDate = (value?: string | Date | null) => {
    if (!value) return '';

    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? '' : excelDateFormatter.format(date);
};

const buildExcelXml = (rows: Array<Array<string | number>>) => {
    const worksheetRows = rows.map((row) => (
        `<Row>${row.map((cell) => `<Cell><Data ss:Type="${typeof cell === 'number' ? 'Number' : 'String'}">${escapeExcelValue(cell)}</Data></Cell>`).join('')}</Row>`
    )).join('');

    return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
 <Worksheet ss:Name="Licencas">
  <Table>${worksheetRows}</Table>
 </Worksheet>
</Workbook>`;
};

const getLicenseBaseValues = (license: ILicenseSimpleProps) => {
    const studentUnitValue = Number(license.studentUnitValue ?? 0);
    const totalValue = Number(license.totalValue ?? ((license.studentsAmount ?? 0) * studentUnitValue));

    return {
        studentUnitValue,
        totalValue,
    };
};

const LicenseList: React.FC<LicenseListProps> = ({ licenses, isSingleMode = false, liveCounters = null, canEditValues = false, onUpdateLicenseValues }) => {
    const [pagination, setPagination] = useState({
        pageNumber: 1,
        pageSize: 12,
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSchoolNames, setSelectedSchoolNames] = useState<string[]>([]);
    const [selectedPlans, setSelectedPlans] = useState<string[]>([]);
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const [draftValues, setDraftValues] = useState<Record<string, DraftValues>>({});
    const [editingField, setEditingField] = useState<{ licenseId: string; field: EditableField } | null>(null);
    const draftValuesRef = useRef<Record<string, DraftValues>>({});

    useEffect(() => {
        draftValuesRef.current = draftValues;
    }, [draftValues]);

    useEffect(() => {
        setPagination((prev) => ({ ...prev, pageNumber: 1 }));
    }, [debouncedSearchTerm, selectedSchoolNames, selectedPlans]);

    useEffect(() => {
        setDraftValues((previousDrafts) => {
            const nextDrafts: Record<string, DraftValues> = {};

            licenses.forEach((license) => {
                const previous = previousDrafts[license.id];
                if (previous?.isSaving) {
                    nextDrafts[license.id] = previous;
                    return;
                }

                const studentUnitValue = Number(license.studentUnitValue ?? 0);
                const totalValue = Number(license.totalValue ?? ((license.studentsAmount ?? 0) * studentUnitValue));

                nextDrafts[license.id] = {
                    studentUnitValue,
                    totalValue,
                    isDirty: false,
                    isSaving: false,
                };
            });

            return nextDrafts;
        });
    }, [licenses]);

    const schoolNameOptions = useMemo(() => (
        Array.from(new Set(licenses.map((license) => license.name).filter(Boolean) as string[]))
            .sort((first, second) => first.localeCompare(second))
    ), [licenses]);

    const planOptions = useMemo(() => (
        Array.from(new Set(licenses.flatMap((license) => (Array.isArray(license.plans) ? license.plans : [])).filter(Boolean)))
            .sort((first, second) => first.localeCompare(second))
    ), [licenses]);

    const filteredLicenses = useMemo(() => {
        const search = debouncedSearchTerm.toLowerCase();

        return licenses.filter((license) => {
            const id = (license.id ?? '').toLowerCase();
            const name = (license.name ?? '').toLowerCase();
            const licensePlans = Array.isArray(license.plans) ? license.plans : [];
            const plans = licensePlans.join(',').toLowerCase();
            const matchesSearch = id.includes(search) || name.includes(search) || plans.includes(search);
            const matchesSchool = selectedSchoolNames.length === 0 || selectedSchoolNames.includes(license.name ?? '');
            const matchesPlan = selectedPlans.length === 0 || licensePlans.some((plan) => selectedPlans.includes(plan));

            return matchesSearch && matchesSchool && matchesPlan;
        });
    }, [licenses, debouncedSearchTerm, selectedSchoolNames, selectedPlans]);

    const paginatedLicenses = useMemo(() => {
        const startIndex = (pagination.pageNumber - 1) * pagination.pageSize;
        const endIndex = startIndex + pagination.pageSize;
        return filteredLicenses.slice(startIndex, endIndex);
    }, [filteredLicenses, pagination.pageNumber, pagination.pageSize]);

    const totalPages = useMemo(
        () => Math.max(1, Math.ceil(filteredLicenses.length / pagination.pageSize)),
        [filteredLicenses.length, pagination.pageSize],
    );

    const formatCurrency = useCallback((value?: number) => currencyFormatter.format(value ?? 0), []);

    const handleExportLicenses = useCallback(() => {
        const rows: Array<Array<string | number>> = [
            [
                'LicenseId',
                'Nome',
                'Planos',
                'Usuarios',
                'Dispositivos',
                'Alunos',
                'Responsaveis',
                'Valor por aluno',
                'Valor total',
                'Ultimo pagamento',
                'Criada em',
                'Expira em',
                'Atualizada em',
            ],
            ...filteredLicenses.map((license) => [
                license.id,
                license.name,
                Array.isArray(license.plans) ? license.plans.join(', ') : '',
                Number(license.usersAmount ?? 0),
                Number(license.devicesAmount ?? 0),
                Number(license.studentsAmount ?? 0),
                Number(license.responsiblesAmount ?? 0),
                Number(license.studentUnitValue ?? 0),
                Number(license.totalValue ?? ((license.studentsAmount ?? 0) * (license.studentUnitValue ?? 0))),
                formatExcelDate(license.lastPayment),
                formatExcelDate(license.createdAt),
                formatExcelDate(license.expiredAt),
                formatExcelDate(license.updatedAt),
            ]),
        ];

        const blob = new Blob([buildExcelXml(rows)], {
            type: 'application/vnd.ms-excel;charset=utf-8;',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `licencas_${new Date().toISOString().slice(0, 10)}.xls`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    }, [filteredLicenses]);

    const handleExportLicense = useCallback((license: ILicenseSimpleProps) => {
        const rows: Array<Array<string | number>> = [
            [
                'LicenseId',
                'Nome',
                'Planos',
                'Usuarios',
                'Dispositivos',
                'Alunos',
                'Responsaveis',
                'Valor por aluno',
                'Valor total',
                'Ultimo pagamento',
                'Criada em',
                'Expira em',
                'Atualizada em',
            ],
            [
                license.id,
                license.name,
                Array.isArray(license.plans) ? license.plans.join(', ') : '',
                Number(license.usersAmount ?? 0),
                Number(license.devicesAmount ?? 0),
                Number(license.studentsAmount ?? 0),
                Number(license.responsiblesAmount ?? 0),
                Number(license.studentUnitValue ?? 0),
                Number(license.totalValue ?? ((license.studentsAmount ?? 0) * (license.studentUnitValue ?? 0))),
                formatExcelDate(license.lastPayment),
                formatExcelDate(license.createdAt),
                formatExcelDate(license.expiredAt),
                formatExcelDate(license.updatedAt),
            ],
        ];

        const blob = new Blob([buildExcelXml(rows)], {
            type: 'application/vnd.ms-excel;charset=utf-8;',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const safeName = (license.name || license.id || 'licenca')
            .replace(/[\\/:*?"<>|]/g, '-')
            .replace(/\s+/g, '_');
        link.href = url;
        link.download = `licenca_${safeName}_${new Date().toISOString().slice(0, 10)}.xls`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    }, []);

    const handleFieldChange = useCallback((license: ILicenseSimpleProps, field: EditableField, rawValue: string) => {
        const numericValue = Number(rawValue);
        if (!Number.isFinite(numericValue) || numericValue < 0) {
            return;
        }

        const studentsAmount = Number(license.studentsAmount ?? 0);

        setDraftValues((previous) => {
            const current = previous[license.id] ?? {
                ...getLicenseBaseValues(license),
                isDirty: false,
                isSaving: false,
            };

            let nextStudentUnitValue = current.studentUnitValue;
            let nextTotalValue = current.totalValue;
            let error: string | undefined;

            if (field === 'studentUnitValue') {
                nextStudentUnitValue = numericValue;
                nextTotalValue = Number((studentsAmount * nextStudentUnitValue).toFixed(2));
            } else {
                nextTotalValue = numericValue;
            }

            return {
                ...previous,
                [license.id]: {
                    ...current,
                    studentUnitValue: nextStudentUnitValue,
                    totalValue: nextTotalValue,
                    isDirty: true,
                    changedField: field,
                    error,
                },
            };
        });
    }, []);

    const handleSave = useCallback(async (license: ILicenseSimpleProps) => {
        const current = draftValuesRef.current[license.id];
        if (!current || !current.isDirty || current.error) {
            return;
        }

        if (!onUpdateLicenseValues) {
            setDraftValues((previous) => ({
                ...previous,
                [license.id]: {
                    ...current,
                    isDirty: false,
                    error: undefined,
                },
            }));
            return;
        }

        setDraftValues((previous) => ({
            ...previous,
            [license.id]: {
                ...previous[license.id],
                isSaving: true,
                error: undefined,
            },
        }));

        try {
            const payload: ILicenseUpdatePayload =
                current.changedField === 'totalValue'
                    ? { totalValue: current.totalValue }
                    : { studentUnitValue: current.studentUnitValue };

            await onUpdateLicenseValues(license.id, payload);
            setDraftValues((previous) => ({
                ...previous,
                [license.id]: {
                    ...previous[license.id],
                    isSaving: false,
                    isDirty: false,
                    changedField: undefined,
                    error: undefined,
                },
            }));
        } catch (error: any) {
            setDraftValues((previous) => ({
                ...previous,
                [license.id]: {
                    ...previous[license.id],
                    isSaving: false,
                    error: error?.message || 'Falha ao salvar alterações.',
                },
            }));
        }
    }, [onUpdateLicenseValues]);

    return (
        <Box
            sx={
                isSingleMode
                    ? {
                        minHeight: { xs: 'calc(100vh - 180px)', md: 'calc(100vh - 220px)' },
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }
                    : undefined
            }
        >
            {!isSingleMode && (
                <Box
                    display="flex"
                    flexDirection={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    gap={1.25}
                    mb={2}
                    sx={{
                        p: 1,
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.10)',
                        backgroundColor: 'rgba(31,41,55,0.72)',
                        boxShadow: '0 10px 24px rgba(0,0,0,0.16)',
                    }}
                >
                    <TextField
                        placeholder="Pesquisar licença"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchRoundedIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            width: '100%',
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                                height: 42,
                                borderRadius: '8px',
                                backgroundColor: 'rgba(15,23,42,0.46)',
                                boxShadow: 'none',
                                transition: 'all 0.2s ease',
                                color: '#fff',
                                '& input::placeholder': {
                                    color: 'rgba(255,255,255,0.62)',
                                    opacity: 1,
                                },
                                '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                                    color: 'rgba(125,211,252,0.9)',
                                },
                                '& fieldset': {
                                    borderColor: 'rgba(255,255,255,0.12)',
                                },
                                '&:hover': {
                                    '& fieldset': {
                                        borderColor: 'rgba(125,211,252,0.38)',
                                    },
                                },
                                '&.Mui-focused': {
                                    boxShadow: '0 0 0 3px rgba(14,165,233,0.12)',
                                    '& fieldset': {
                                        borderColor: 'rgba(125,211,252,0.70)',
                                    },
                                },
                            },
                        }}
                    />
                    <Autocomplete
                        multiple
                        limitTags={1}
                        options={schoolNameOptions}
                        value={selectedSchoolNames}
                        onChange={(_, value) => setSelectedSchoolNames(value)}
                        noOptionsText="Nenhuma escola"
                        renderTags={(value, getTagProps) => {
                            if (value.length === 0) return null;

                            const label = value.length === 1 ? value[0] : `${value.length} licenças`;
                            return (
                                <Chip
                                    {...getTagProps({ index: 0 })}
                                    label={label}
                                    size="small"
                                />
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Licença"
                                size="small"
                            />
                        )}
                        sx={{
                            width: { xs: '100%', sm: 250 },
                            flexShrink: 0,
                            '& .MuiOutlinedInput-root': {
                                height: 42,
                                overflow: 'hidden',
                                borderRadius: '8px',
                                backgroundColor: 'rgba(15,23,42,0.46)',
                                color: '#fff',
                                '& fieldset': {
                                    borderColor: 'rgba(255,255,255,0.12)',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(125,211,252,0.38)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'rgba(125,211,252,0.70)',
                                },
                            },
                            '& .MuiInputBase-input::placeholder': {
                                color: 'rgba(255,255,255,0.62)',
                                opacity: 1,
                            },
                            '& .MuiChip-root': {
                                maxWidth: 140,
                                height: 24,
                                color: '#fff',
                                backgroundColor: 'rgba(255,255,255,0.10)',
                            },
                            '& .MuiSvgIcon-root': {
                                color: 'rgba(255,255,255,0.72)',
                            },
                        }}
                    />
                    <Autocomplete
                        multiple
                        limitTags={1}
                        options={planOptions}
                        value={selectedPlans}
                        onChange={(_, value) => setSelectedPlans(value)}
                        noOptionsText="Nenhum plano"
                        renderTags={(value, getTagProps) => {
                            if (value.length === 0) return null;

                            const label = value.length === 1 ? value[0] : `${value.length} planos`;
                            return (
                                <Chip
                                    {...getTagProps({ index: 0 })}
                                    label={label}
                                    size="small"
                                />
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Plano"
                                size="small"
                            />
                        )}
                        sx={{
                            width: { xs: '100%', sm: 210 },
                            flexShrink: 0,
                            '& .MuiOutlinedInput-root': {
                                height: 42,
                                overflow: 'hidden',
                                borderRadius: '8px',
                                backgroundColor: 'rgba(15,23,42,0.46)',
                                color: '#fff',
                                '& fieldset': {
                                    borderColor: 'rgba(255,255,255,0.12)',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(125,211,252,0.38)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'rgba(125,211,252,0.70)',
                                },
                            },
                            '& .MuiInputBase-input::placeholder': {
                                color: 'rgba(255,255,255,0.62)',
                                opacity: 1,
                            },
                            '& .MuiChip-root': {
                                maxWidth: 120,
                                height: 24,
                                color: '#fff',
                                backgroundColor: 'rgba(255,255,255,0.10)',
                            },
                            '& .MuiSvgIcon-root': {
                                color: 'rgba(255,255,255,0.72)',
                            },
                        }}
                    />
                    <Tooltip title="Exportar licencas filtradas para Excel">
                        <span>
                            <Button
                                variant="contained"
                                startIcon={<FileDownloadRoundedIcon fontSize="small" />}
                                onClick={handleExportLicenses}
                                disabled={filteredLicenses.length === 0}
                                sx={{
                                    minWidth: { xs: '100%', sm: 150 },
                                    height: 42,
                                    borderRadius: '8px',
                                    px: 2,
                                    textTransform: 'none',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 800,
                                    color: '#082f49',
                                    backgroundColor: '#7dd3fc',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: '#bae6fd',
                                        boxShadow: 'none',
                                    },
                                    '&.Mui-disabled': {
                                        color: 'rgba(255,255,255,0.36)',
                                        backgroundColor: 'rgba(255,255,255,0.08)',
                                    },
                                }}
                            >
                                Exportar
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            )}

            <Box
                display="grid"
                gridTemplateColumns={
                    isSingleMode
                        ? { xs: '1fr', lg: 'minmax(760px, 1fr)' }
                        : { xs: '1fr', sm: `repeat(auto-fill, minmax(${ADMIN_CARD_MIN_WIDTH}px, 1fr))` }
                }
                gap={2}
                sx={isSingleMode ? { maxWidth: 980, mx: 'auto' } : undefined}
            >
                {paginatedLicenses.map((license) => {
                    const draft = draftValues[license.id];
                    const studentUnitValue = draft?.studentUnitValue ?? Number(license.studentUnitValue ?? 0);
                    const totalValue = draft?.totalValue ?? Number(license.totalValue ?? ((license.studentsAmount ?? 0) * studentUnitValue));
                    const isSaving = draft?.isSaving ?? false;
                    const error = draft?.error;
                    const isEditingStudentUnit = canEditValues && editingField?.licenseId === license.id && editingField.field === 'studentUnitValue';
                    const isEditingTotalValue = canEditValues && editingField?.licenseId === license.id && editingField.field === 'totalValue';

                    return (
                        <LicenseCard
                            key={license.id}
                            license={license}
                            isSingleMode={isSingleMode}
                            canEditValues={canEditValues}
                            liveCounters={liveCounters}
                            studentUnitValue={studentUnitValue}
                            totalValue={totalValue}
                            isSaving={isSaving}
                            error={error}
                            isEditingStudentUnit={isEditingStudentUnit}
                            isEditingTotalValue={isEditingTotalValue}
                            formatCurrency={formatCurrency}
                            onFieldChange={handleFieldChange}
                            onSave={handleSave}
                            onSetEditingField={setEditingField}
                            onExportLicense={handleExportLicense}
                        />
                    );
                })}
            </Box>

            {!isSingleMode && (
                <Box
                    sx={{
                        mt: 3,
                        mb: 1,
                        mx: 'auto',
                        width: 'fit-content',
                        px: 1.5,
                        py: 0.75,
                        border: '1px solid',
                        borderColor: 'rgba(255,255,255,0.12)',
                        borderRadius: 2,
                        backgroundColor: 'var(--app-surface-2)',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                    }}
                >
                    <Pagination
                        count={totalPages}
                        page={pagination.pageNumber}
                        onChange={(_, value) => setPagination({ ...pagination, pageNumber: value })}
                        color="primary"
                        shape="rounded"
                    />
                </Box>
            )}
        </Box>
    );
};

export default LicenseList;
